/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineMap, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useRegionalBlocksContext } from "../../context/RegionalBlocksContext";
import { pushOneRegionalBlocks } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

// Note: the legacy Overview used `trade_organization_id` as the row key AND
// the delete-API param. Preserving that shape here verbatim so no existing
// action behaviour changes.
type RegionalBlock = {
  trade_organization_id: number | string;
  name: string;
  [key: string]: unknown;
};

const addRoute = "/regional/blocks/add";
const editRoute = "/regional/blocks/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    regionalBlocks,
    count,
    fetchRegionalBlocks,
    deleteRegionalBlocks,
    updateRegionalBlocksState,
  } = useRegionalBlocksContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!regionalBlocks) fetchRegionalBlocks(page, pageSize);
  }, [regionalBlocks]);

  const rows = useMemo<RegionalBlock[] | null>(() => {
    if (!Array.isArray(regionalBlocks)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return regionalBlocks as RegionalBlock[];
    return (regionalBlocks as RegionalBlock[]).filter((r) =>
      r.name?.toString().toLowerCase().includes(q),
    );
  }, [regionalBlocks, query]);

  const columns: Column<RegionalBlock>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <span className="text-[10px] font-semibold">
              {row.name?.toString().slice(0, 2).toUpperCase() || "RB"}
            </span>
          </span>
          <span className="font-medium text-gray-900">{row.name}</span>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: RegionalBlock) => {
    const response: any = await deleteRegionalBlocks(row.trade_organization_id);
    if (response?.status === 200 && Array.isArray(regionalBlocks)) {
      updateRegionalBlocksState(
        (regionalBlocks as RegionalBlock[]).filter(
          (r) => r.trade_organization_id !== row.trade_organization_id,
        ),
      );
    }
  };

  const rowActions = (row: RegionalBlock): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneRegionalBlocks(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name}?`,
        description:
          "The regional block will be removed from every record that references it.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<RegionalBlock>
      title="Regional Blocks"
      subtitle="Browse, add and manage regional economic blocks."
      icon={<HiOutlineMap className="h-5 w-5" />}
      actions={[
        { label: "Add regional block", variant: "primary", href: addRoute },
      ]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.trade_organization_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search regional blocks…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchRegionalBlocks(p, ps);
        },
      }}
      empty="No regional blocks yet."
    />
  );
};

export default Overview;
